import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  CheckBox,
  Inscription,
  RadioButton,
  WarningIcon,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT, EMPTY_STATE_TEXT } from '@/common/constants';

import useLinkedAccountsStore from '../useLinkedAccounts.store';
import { LinkedAccount } from '../useSearchedAccounts.query';

import TileCell from './TileCell';

const TileWrapper = styled(Box)`
  > div:not(:first-child) {
    padding-left: ${({ theme }) => theme.spacings.spacing12};
  }
`;

export enum AccountTileMode {
  LINKED = 'LINKED',
  SUGGESTED = 'SUGGESTED',
  MANUAL_SEARCH = 'MANUAL_SEARCH',
  GROUPED_ACCOUNT = 'GROUPED_ACCOUNT',
}

interface Props {
  account: LinkedAccount;
  errorMessage?: string;
  isEditMode?: boolean;
  mode?: AccountTileMode;
  isPrimaryDisabled?: boolean;
}

const AccountTile: React.FC<Props> = ({ account, mode, isPrimaryDisabled, isEditMode, errorMessage }) => {
  const [isExpanded, setIsExpanded] = useState(mode !== AccountTileMode.GROUPED_ACCOUNT);
  const {
    primaryEmployeeId,
    linkedEmployeeIds,
    handleSetPrimaryEmployeeId,
    handleSetLinkedEmployeeIds,
  } = useLinkedAccountsStore();
  const isPrimary = useMemo(() => (account?.isPrimaryLinkedAccount && !primaryEmployeeId)
    || (account?.employeeId === primaryEmployeeId), [primaryEmployeeId, account]);
  const isLinked = useMemo(() => linkedEmployeeIds?.some((linkedEmployeeId) => linkedEmployeeId === account?.employeeId),
    [account, linkedEmployeeIds]);
  const onLinkAccount = useCallback(() => {
    if (!account?.employeeId) return;
    const ids = linkedEmployeeIds?.filter((linkedEmployeeId) => linkedEmployeeId !== account.employeeId) || [];
    if (isLinked) {
      return handleSetLinkedEmployeeIds(ids);
    }
    handleSetLinkedEmployeeIds([ ...ids, account.employeeId ]);
  }, [isLinked, account, linkedEmployeeIds, handleSetLinkedEmployeeIds]);
  const warningMessage = useMemo(() => {
    const missedProps = [
      account?.employerEmployeeId ? '' : 'EID',
      account?.partnerEmployeeId ? '' : 'PID',
      account?.ssn ? '' : 'SSN',
    ].filter((item) => item);

    if (missedProps.length) return `${missedProps.join(', ')} ${missedProps.length > 1 ? 'don\'t match' : 'doesn\'t match'}`;
  }, [account]);

  return (
    <Box
      data-testId={`AccountTile_${account?.employeeId}`}
      border={{ size: 'small', color: 'border2' }}
      round="8px"
      background="canvas"
      pad={{ horizontal: 'spacing24', vertical: 'spacing16' }}
    >
      <Box>
        {errorMessage ? (
          <Box data-testId={`accountTileErrorMessage_${account?.employeeId}`} margin={{ bottom: 'spacing8' }} direction="row" align="center">
            <AlertIcon size="12px" />
            <Inscription margin={{ left: 'spacing4' }} color="danger" lineHeight="20px">{errorMessage}</Inscription>
          </Box>
        ) : null}

        <Box direction="row">
          {mode !== AccountTileMode.LINKED && mode !== AccountTileMode.GROUPED_ACCOUNT ? (
            <Box height="42px" margin={{ right: 'spacing12' }} justify="center">
              <CheckBox
                onChange={onLinkAccount}
                disabled={!isEditMode}
                data-testId={`link_account_checkbox_${account?.employeeId}`}
              />
            </Box>
          ) : null}

          <Box flex>
            <Box direction="row" align="center">
              <Box onClick={() => setIsExpanded(!isExpanded)} width="24px" height="24px" margin={{ right: 'spacing12' }}>
                {isExpanded ? <ArrowUpIcon color="iconPrimary" /> : <ArrowDownIcon color="iconPrimary" />}
              </Box>
              <TileWrapper direction="row" flex>
                <TileCell head="Employee" title={`${account?.firstName} ${account?.lastName}`.trim()} isBold />
                <TileCell head="Employer" title={account?.employerName} isBold />
                <TileCell head="Employment status" status={account?.employmentStatus} isBold />
              </TileWrapper>
            </Box>
            {isExpanded ? (
              <Box margin={{ left: '36px', top: 'spacing8' }}>
                <TileWrapper direction="row" flex>
                  <TileCell head="SSN" title={account?.ssn || EMPTY_STATE_TEXT} />
                  <TileCell head="EE ID" title={account?.employerEmployeeId || EMPTY_STATE_TEXT} />
                  <TileCell head="PE ID" title={account?.partnerEmployeeId || EMPTY_STATE_TEXT} />
                </TileWrapper>
                <TileWrapper direction="row" flex margin={{ top: 'spacing8' }}>
                  <TileCell head="Date of Birth" title={dayjs(account?.dateOfBirth).format(DEFAULT_DATE_FORMAT)} />
                  <TileCell head="Address" title={account?.address?.line1} basis="2/3" />
                </TileWrapper>
              </Box>
            ) : null}
            {mode === AccountTileMode.LINKED ? (
              <Box margin={{ left: '36px', top: 'spacing16' }} data-testId={`radioButton_primary${account?.employeeId}`}>
                <RadioButton
                  name={`primary-account-${account.employeeId}`}
                  onChange={() => handleSetPrimaryEmployeeId(account?.employeeId)}
                  checked={isPrimary}
                  label={(
                    <Box justify="center" height="24px" data-testId={`radioButton_primary_label_${account?.employeeId}`}>
                      <Inscription color="textBody" lineHeight="20px">Primary account</Inscription>
                    </Box>
                  )}
                  disabled={!isEditMode || isPrimaryDisabled}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
        {(mode === AccountTileMode.MANUAL_SEARCH || mode === AccountTileMode.GROUPED_ACCOUNT) && warningMessage ? (
          <Box
            data-testId="AccountTile_warningMessage"
            margin={{ top: 'spacing18' }}
            pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
            border={{ size: 'small', color: 'warningBorder' }}
            background="warningContainer"
            round="8px"
            direction="row"
            align="center"
          >
            <WarningIcon />
            <Inscription
              margin={{ left: 'spacing8' }}
              weight="bold"
              lineHeight="20px"
              color="textBody"
            >
              {warningMessage}
            </Inscription>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default AccountTile;
